import React from 'react';
import { X, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../../ui/Button';
import { allergensOfIngredient } from '../../../../shared/allergenReference.js';
import type { DishIngredient } from './MenuCatalogEditors';

interface AuditDish { id: string; name: string; category: string; ingredients: DishIngredient[]; traces?: string[]; }

/**
 * Contrôle du catalogue avant de valider une carte.
 *
 * Répond à la question d'Emeni (2026-08-23) : « demain je change de carte, comment créer ces plats sans
 * aucune erreur pour les allergènes et les restrictions ? ». Vérifier plat par plat ne suffit pas — sur
 * vingt fiches, un ingrédient oublié passe inaperçu. Cet écran regarde tout le catalogue d'un coup et ne
 * remonte que ce qui empêcherait une alerte de se déclencher :
 *
 *  1. les ingrédients qu'aucun allergène ne reconnaît (« Boudoir » : plein de gluten, mot inconnu du système) ;
 *  2. les plats sans aucun ingrédient — ils ne peuvent alerter personne ;
 *  3. les plats sans traces déclarées, à confirmer plutôt qu'à corriger.
 *
 * Le point 1 se règle sur place : un clic rattache l'ingrédient à un allergène, pour tous les plats à la fois.
 */
export function MenuCatalogAudit({ dishes, allergenTerms, allergenNames, onLink, onClose }: {
    dishes: AuditDish[];
    allergenTerms: any[];
    allergenNames: string[];
    onLink: (ingredient: string, allergen: string) => Promise<void>;
    onClose: () => void;
}) {
    const [linking, setLinking] = React.useState('');

    // Un ingrédient peut revenir dans plusieurs plats : on le traite une fois, en gardant la liste des plats
    // concernés pour montrer la portée du problème.
    const unrecognized = React.useMemo(() => {
        const map = new Map<string, { name: string; dishes: string[] }>();
        dishes.forEach((d) => (d.ingredients || []).forEach((i) => {
            if (allergensOfIngredient(i, allergenTerms).length > 0) return;
            const key = i.name.toLowerCase();
            if (!map.has(key)) map.set(key, { name: i.name, dishes: [] });
            const entry = map.get(key)!;
            if (!entry.dishes.includes(d.name)) entry.dishes.push(d.name);
        }));
        return Array.from(map.values()).sort((a, b) => b.dishes.length - a.dishes.length || a.name.localeCompare(b.name, 'fr'));
    }, [dishes, allergenTerms]);

    const emptyDishes = dishes.filter((d) => !(d.ingredients || []).length);
    const noTraces = dishes.filter((d) => (d.ingredients || []).length && !(d.traces || []).length);

    const link = async (ingredient: string, allergen: string) => {
        setLinking(ingredient);
        try { await onLink(ingredient, allergen); } finally { setLinking(''); }
    };

    const clean = unrecognized.length === 0 && emptyDishes.length === 0;

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={onClose}>
            <div className="bg-surface border border-border w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border shrink-0">
                    <h3 className="text-sm font-bold text-content">Contrôle du catalogue — {dishes.length} plat(s)</h3>
                    <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-surface-soft text-content-muted hover:text-content"><X size={16} /></button>
                </div>

                <div className="overflow-y-auto px-4 py-3.5 flex-1 space-y-4">
                    {clean && (
                        <div className="flex items-start gap-2 bg-green-500/10 border border-green-500/30 rounded-xl p-3">
                            <CheckCircle2 size={16} className="text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                            <p className="text-xs text-content">
                                <strong className="text-green-700 dark:text-green-400">Tous les ingrédients sont reconnus.</strong> Chaque plat peut
                                déclencher les alertes correspondantes. Vérifie tout de même que la liste d'ingrédients de chaque plat est complète :
                                le système ne peut pas deviner un ingrédient que tu n'as pas saisi.
                            </p>
                        </div>
                    )}

                    {unrecognized.length > 0 && (
                        <div className="space-y-2">
                            <div>
                                <h4 className="text-[11px] font-black text-content uppercase tracking-widest flex items-center gap-1.5">
                                    <AlertTriangle size={12} className="text-amber-600" /> {unrecognized.length} ingrédient(s) sans allergène
                                </h4>
                                <p className="text-[10px] text-content-muted">
                                    Ces ingrédients ne déclencheront aucune alerte. Beaucoup sont inoffensifs (citron, salade, herbes) — il n'y a
                                    alors rien à faire. Mais si l'un d'eux contient un allergène, rattache-le : le rattachement vaut pour tous tes plats.
                                </p>
                            </div>
                            {unrecognized.map((u) => (
                                <div key={u.name} className="bg-surface-soft border border-border rounded-xl p-2.5 space-y-1.5">
                                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                        <span className="text-xs font-bold text-content">{u.name}</span>
                                        <span className="text-[10px] text-content-muted">{u.dishes.length} plat(s) : {u.dishes.slice(0, 3).join(', ')}{u.dishes.length > 3 ? '…' : ''}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {linking === u.name ? (
                                            <span className="text-[10px] text-content-muted flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Rattachement…</span>
                                        ) : allergenNames.map((a) => (
                                            <button key={a} type="button" onClick={() => link(u.name, a)}
                                                className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-border text-content-muted hover:text-content hover:border-brand-500">
                                                + {a}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {emptyDishes.length > 0 && (
                        <div className="space-y-1">
                            <h4 className="text-[11px] font-black text-content uppercase tracking-widest flex items-center gap-1.5">
                                <AlertTriangle size={12} className="text-red-500" /> {emptyDishes.length} plat(s) sans aucun ingrédient
                            </h4>
                            <p className="text-[10px] text-content-muted">
                                Sans ingrédient, un plat ne peut alerter personne — il sera proposé à tous les convives, quelles que soient leurs allergies.
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {emptyDishes.map((d) => (
                                    <span key={d.id} className="text-[10px] font-semibold bg-red-500/10 border border-red-500/30 rounded-full px-2 py-0.5 text-red-500">{d.name}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {noTraces.length > 0 && (
                        <div className="space-y-1">
                            <h4 className="text-[11px] font-black text-content uppercase tracking-widest">{noTraces.length} plat(s) sans traces déclarées</h4>
                            <p className="text-[10px] text-content-muted">
                                À confirmer plutôt qu'à corriger : un plat frit ou un dessert de pâtisserie a presque toujours des traces
                                (friteuse partagée, plan de travail commun). Un plat qui n'en a réellement aucune peut rester ainsi.
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {noTraces.map((d) => (
                                    <span key={d.id} className="text-[10px] font-semibold bg-surface-soft border border-border rounded-full px-2 py-0.5 text-content-muted">{d.name}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border shrink-0">
                    <Button size="sm" onClick={onClose}>Fermer</Button>
                </div>
            </div>
        </div>
    );
}
